import { yupResolver } from '@hookform/resolvers/yup';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { DialogActions, DialogContent, Grid } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IComposableDashboard, IComposableDashboardContext } from 'wisestep-api';
import {
  WsDialog,
  WsDialogSize,
  Item,
  WsTypography,
  WsTypoVariants,
  ControlledTextInput,
  WsButton,
  WsColors,
} from 'wisestep-base';
import * as yup from 'yup';

import { useComposableDashboardsContext } from '../../hooks/useComposableDashboardsContext';

const schema = yup.object().shape({
  dashboardName: yup.string().required('Dashboard name is required'),
  description: yup
    .string()
    .max(255, 'Description must be at most 255 characters long')
    .required('Description is required'),
});

interface SaveDialogProps {
  openSaveModal: boolean;
  setSaveModal: Dispatch<SetStateAction<boolean>>;
  saveDashboard: (newDashboard: IComposableDashboard) => Promise<void>;
  update: boolean;
  customUpdateText?: string;
}

const SaveDialog: React.FC<SaveDialogProps> = ({
  openSaveModal,
  setSaveModal,
  saveDashboard,
  update,
  customUpdateText,
}) => {
  const { selectedDashboard } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const methods = useForm({
    defaultValues: {
      dashboardName: selectedDashboard?.name !== '' ? selectedDashboard?.name : undefined,
      description: selectedDashboard?.description ?? '',
    },
    resolver: yupResolver(schema),
  });
  const { handleSubmit } = methods;

  const onSubmitHandle = (data) =>
    saveDashboard({
      ...selectedDashboard,
      name: data.dashboardName,
      description: data.description,
    });

  return (
    <FormProvider {...methods}>
      <WsDialog openDialog={openSaveModal} size={WsDialogSize.SMALL}>
        <Grid
          container
          flexDirection="column"
          component="form"
          onSubmit={handleSubmit(onSubmitHandle)}
        >
          <DialogContent>
            <Item sx={{ pl: '45%', pr: '45%' }}>
              <SaveOutlinedIcon sx={{ color: WsColors.TheBlues400, fontSize: '60px' }} />
            </Item>
            <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                {update ? customUpdateText ?? 'Update Dashboard' : 'Save Dashboard'}
              </WsTypography>
            </Item>
            <Item sx={{ margin: 'auto' }}>
              <ControlledTextInput
                name="dashboardName"
                placeholder="Dashboard Name"
                required
                fullWidth
              />
            </Item>
            <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                {'Dashboard Description'}
              </WsTypography>
            </Item>
            <Item sx={{ margin: 'auto' }}>
              <ControlledTextInput
                name="description"
                placeholder="Description"
                required
                fullWidth
                disabled={selectedDashboard.createdBy == 'Default' ? true : false}
              />
            </Item>
          </DialogContent>
          <DialogActions>
            <WsButton size="large" variant="outlined" onClick={() => setSaveModal(false)}>
              Cancel
            </WsButton>
            <WsButton size="large" variant="contained" type="submit">
              {update ? 'Update' : 'Save'}
            </WsButton>
          </DialogActions>
        </Grid>
      </WsDialog>
    </FormProvider>
  );
};

export default SaveDialog;
